import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import integrationsService from '../../services/integrationsService';
import Button from '../shared/Button';
import Toggle from '../shared/Toggle';
import TemplateStreamingClientsSection from '../streaming/TemplateStreamingClientsSection';
import IntegrationLogsTab from '../IntegrationLogsTab';
import '../shared/Tabs.css';
import './EventPoller.css';
import { formatDateTime } from '../../utils/datetime';
import { apiErrorMessage } from '../../utils/apiError';
import '../PartnerSyncTemplates.css';
import '../streaming/StreamingClients.css';

// HOW THE TEMPLATE'S SOURCE EVENTS REACH THE HUB (V688) — opened from the source adapter's Events button on the pipeline
// template's design.
//
// POLL OR STREAM, NEVER BOTH. OHIP hands each business event over once, so a template either polls (the event poller
// below) or streams (a streaming client per hotel, TemplateStreamingClientsSection). The choice is made at the top and
// only its half is shown; the API refuses the switch while the other is still running.
//
// TWO TABS (?tab=): Settings — the choice and its half; Processed — what the event poller did with each business event
// (its `poller.event` steps in the pipeline log, each opening on the event's whole transaction). Processed is disabled,
// not hidden, while the template does not poll.
//
// POLL — THE OHIP ADAPTER'S EVENT POLLER (V635):
// SET ONCE, RUN PER HOTEL. The settings and the switch are the template's; the poller runs for every ACTIVE hotel
// implementing it, each dequeuing its own OHIP business events with its own source credential and hotel id, reading
// each changed record from OHIP and handing it to the source's adapter, whose routes carry it on. Each hotel's
// readiness and last tick are listed below — a hotel that is not ready is skipped, with its reason.
//
// A FULL SCREEN, NOT A MODAL (STYLE.md). Superadmin only (the API refuses anyone else).

const TABS = ['settings', 'processed'];

// A length of time in words: "60 s", "3 min", "2 h 5 min".
const seconds = (s) => {
    if (s == null) return '—';
    if (s < 120) return `${Math.round(s)} s`;
    const m = Math.round(s / 60);
    return m < 120 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
};
const TICK_CLASS = (t) => (t.outcome === 'ok' ? (t.events > 0 ? 'events' : 'ok') : t.outcome);
const TICK_WORDS = (t) => (t.outcome === 'ok'
    ? `${t.events} event(s), ${t.carried} carried${t.failed ? `, ${t.failed} failed` : ''}`
    : { busy: 'skipped — the previous poll was still running', skipped: 'not ready', error: 'failed' }[t.outcome] || t.outcome);

const formOf = (p) => ({
    batchLimit: String((p && p.batch_limit) || 20),
    // Minutes on the screen; seconds on the wire (pollIntervalSeconds, V698). Default every 5 minutes.
    pollMinutes: String(Math.round(((p && p.poll_interval_seconds) || 300) / 60)),
    lookaheadDays: String((p && p.lookahead_days) || 540),
    reconcileDays: String((p && p.reconcile_days) || 90),
    occupancies: ((p && p.occupancies) || [1, 2]).join(', '),
});

const TemplateEventDeliveryPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    // ?from= says where the template's own Back goes; ?tab= is this page's (STYLE.md: the selected tab lives in the URL).
    const [searchParams, setSearchParams] = useSearchParams();
    const from = searchParams.get('from');
    const backPath = `/pipeline-templates/${id}/edit${from ? `?from=${encodeURIComponent(from)}` : ''}`;
    const [tab, setTab] = useState(() => (TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : TABS[0]));
    const changeTab = (next) => {
        setTab(next);
        setSearchParams({ tab: next, ...(from ? { from } : {}) }, { replace: true });
    };

    const [data, setData] = useState(null);
    const [form, setForm] = useState(formOf(null));
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            const d = await integrationsService.getEventPoller(id);
            setData(d);
            setForm(formOf(d.poller));
        } catch (e) {
            setError(apiErrorMessage(e));
        }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
    const body = (extra = {}) => ({
        batchLimit: parseInt(form.batchLimit, 10),
        pollIntervalSeconds: parseInt(form.pollMinutes, 10) * 60,
        lookaheadDays: parseInt(form.lookaheadDays, 10),
        reconcileDays: parseInt(form.reconcileDays, 10),
        occupancies: form.occupancies.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !Number.isNaN(n)),
        ...extra,
    });
    const run = async (what, extra) => {
        setBusy(what);
        setError(null);
        try {
            const d = await integrationsService.saveEventPoller(id, body(extra));
            setData(d);
            setForm(formOf(d.poller));
        } catch (e) {
            setError(apiErrorMessage(e));
        } finally {
            setBusy(null);
        }
    };

    const p = data && data.poller;
    const hotels = (data && data.hotels) || [];
    const cache = !!(data && data.cache);
    const enabled = !!p && p.enabled === 'Y';
    const delivery = (p && p.delivery) || null;
    const offers = (data && data.offers) || { poll: false, stream: false };
    const choose = (next) => run('delivery', { delivery: next });

    // POLL NOW — queued for the poller; the page reads the result back from each hotel's last tick, twice, since a poll
    // can take up to its minute. A hotel the schedule is polling at that moment shows "skipped, still running".
    const [polled, setPolled] = useState(null);
    const timers = useRef([]);
    useEffect(() => () => timers.current.forEach(clearTimeout), []);
    const pollNow = async (implementationIds) => {
        const what = implementationIds ? `poll-${implementationIds[0]}` : 'poll';
        setBusy(what);
        setError(null);
        try {
            const r = await integrationsService.pollEventsNow(id, implementationIds);
            setPolled(`Polling ${r.queued.map(h => h.name).join(', ')} now — the result shows below within a minute.`);
            timers.current.forEach(clearTimeout);
            // The data only — a settings field being edited is left as it is.
            const refresh = () => integrationsService.getEventPoller(id).then(setData).catch(() => {});
            timers.current = [15000, 60000].map(ms => setTimeout(refresh, ms));
        } catch (e) {
            setError(apiErrorMessage(e));
        } finally {
            setBusy(null);
        }
    };
    // EVERYONE WHO CAN SEE THE TEMPLATE READS THIS PAGE (their own hotels); only a superadmin changes it.
    const canEdit = !!(data && data.canEdit);
    const scoped = {
        listLogs: (params) => integrationsService.listEventPollerProcessed(id, params),
        loadSteps: (groupId) => integrationsService.getEventPollerProcessedGroup(id, groupId),
    };

    return (
        <div className="pst-container">
            <div className="pst-detail-header pst-detail-header-stacked">
                <button className="pst-back-btn" onClick={() => navigate(backPath)}>
                    ← Back to {data ? data.template.name : 'the pipeline template'}
                </button>
                <div className="pst-detail-header-main">
                    <div className="pst-detail-title">Events</div>
                </div>
            </div>

            {error && <div className="os-msg os-msg-error">{error}</div>}
            {!data && !error && <p className="cm-message">Loading…</p>}

            {data && (
                <div className="hc-tabs">
                    <button type="button" className={`hc-tab${tab === 'settings' ? ' hc-tab-active' : ''}`}
                            onClick={() => changeTab('settings')}>
                        <span className="hc-tab-label">Settings</span>
                    </button>
                    <button type="button" className={`hc-tab${tab === 'processed' ? ' hc-tab-active' : ''}`}
                            disabled={delivery !== 'POLL'}
                            title={delivery !== 'POLL' ? 'Only a template that polls its events has an event poller' : undefined}
                            onClick={() => changeTab('processed')}>
                        <span className="hc-tab-label">Processed</span>
                    </button>
                </div>
            )}

            {/* PROCESSED — every business event the poller dequeued for this template's hotels, and what became of it. */}
            {data && tab === 'processed' && delivery === 'POLL' && (
                <div className="hc-tab-panel">
                    <IntegrationLogsTab integrationId={id} step="poller" scopeName={`the event poller of ${data.template.name}`}
                        {...scoped} canPurge={false} canResend={false}
                        emptyText="The event poller has processed nothing for these filters. Each business event it dequeues is listed here (kept 14 days)." />
                </div>
            )}

            {/* THE CHOICE — one or the other, each offered only when the source's system can do it. */}
            {data && (tab === 'settings' || delivery !== 'POLL') && (
                <div className="stc-uses">
                    <div className="stc-uses-title">How events reach the hub</div>
                    {!canEdit && <div>{delivery === 'POLL' ? 'Polled — the hub polls each hotel\'s business events'
                        : delivery === 'STREAM' ? 'Streamed — a streaming client per hotel' : 'Not chosen yet'}</div>}
                    {canEdit && <div className="stc-actions">
                        {offers.poll && (
                            <Button variant={delivery === 'POLL' ? 'primary' : 'secondary'} disabled={!!busy || delivery === 'POLL'}
                                    className="sa-only" onClick={() => choose('POLL')}>
                                {delivery === 'POLL' ? 'Polled' : 'Poll business events'}
                            </Button>
                        )}
                        {offers.stream && (
                            <Button variant={delivery === 'STREAM' ? 'primary' : 'secondary'}
                                    disabled={!!busy || delivery === 'STREAM' || enabled}
                                    title={enabled ? 'Switch the poller off first' : undefined}
                                    className="sa-only" onClick={() => choose('STREAM')}>
                                {delivery === 'STREAM' ? 'Streamed' : 'Stream'}
                            </Button>
                        )}
                    </div>}
                    <div className="scq-meta">
                        {!offers.poll && !offers.stream
                            ? 'This template\'s source can neither be polled nor streamed.'
                            : delivery
                                ? 'One or the other: OHIP hands each event over once, so a template never polls and streams at once.'
                                : canEdit ? 'Not chosen yet — choose how this template\'s events reach the hub.' : ''}
                    </div>
                </div>
            )}

            {data && delivery === 'STREAM' && <TemplateStreamingClientsSection templateId={id} />}

            {data && delivery === 'POLL' && tab === 'settings' && (
                <div className="stc-panel">
                    <p className="cm-message">
                        Polls OHIP&rsquo;s business events for every active hotel implementing this template, each with
                        its own source credential and hotel id, reads each changed record in full and hands it to the
                        source&rsquo;s adapter, whose routes carry it on.
                    </p>

                    <div className="stc-grid">
                        <div><div className="scq-meta">Polling</div>
                            {canEdit
                                ? <Toggle on={enabled} onLabel="Enabled" offLabel={p ? 'Disabled' : 'Not set up'} className="sa-only"
                                          busy={busy === 'toggle'} onChange={(on) => run('toggle', { enabled: on })} />
                                : <span className={`scq-status scq-status-${enabled ? 'returned' : 'queued'}`}>{enabled ? 'Enabled' : 'Disabled'}</span>}</div>
                        {canEdit && <div><div className="scq-meta">Now</div>
                            <Button variant="success" className="sa-only" disabled={!!busy}
                                    title="Poll every active hotel's business events now, without waiting for the minute"
                                    onClick={() => pollNow()}>
                                {busy === 'poll' ? 'Queuing…' : 'Poll now'}
                            </Button></div>}
                        <div><div className="scq-meta">Cadence</div>
                            every {seconds(data.scheduleSeconds)}, each hotel
                            <div className="scq-meta">{p ? `up to ${p.batch_limit} events per dequeue` : ''}</div></div>
                        <div><div className="scq-meta">Changed</div>{p ? formatDateTime(p.updated_at) : '—'}
                            {p && p.updated_by && <div className="scq-meta">{p.updated_by}</div>}</div>
                    </div>

                    {polled && <div className="os-msg">{polled}</div>}

                    {canEdit && <div className="sa-only-panel stc-panel">
                    <div className="scq-form stc-settings">
                        <label>Poll each hotel every (minutes)
                            <input type="number" min={1} max={60} value={form.pollMinutes} onChange={set('pollMinutes')}
                                   title="The poller ticks every minute; each hotel is polled on every Nth tick. Poll now does not wait." />
                        </label>
                        <label>Events per dequeue
                            <input type="number" min={1} max={100} value={form.batchLimit} onChange={set('batchLimit')} />
                        </label>
                        {cache && <>
                            <label>Fill ahead (days)
                                <input type="number" min={1} max={730} value={form.lookaheadDays} onChange={set('lookaheadDays')} />
                            </label>
                            <label>Re-price nightly (days)
                                <input type="number" min={1} max={730} value={form.reconcileDays} onChange={set('reconcileDays')} />
                            </label>
                            <label>Price for adults
                                <input value={form.occupancies} onChange={set('occupancies')} placeholder="1, 2" />
                            </label>
                        </>}
                    </div>

                    <div className="stc-actions">
                        <Button variant="primary" disabled={!!busy} onClick={() => run('save')}>
                            {busy === 'save' ? 'Saving…' : 'Save'}
                        </Button>
                        {p && cache && <Button variant="secondary" disabled={!!busy || !enabled}
                                               title="Queue every hotel's whole lookahead again on the next tick"
                                               onClick={() => run('rehydrate', { rehydrate: true })}>
                            {busy === 'rehydrate' ? 'Queuing…' : 'Refill cache'}
                        </Button>}
                    </div>
                    </div>}

                    {/* EACH HOTEL — what it polls OHIP as, whether it is ready, and its last tick. */}
                    <table className="acp-table">
                        <thead>
                            <tr>
                                <th>Hotel</th><th>Status</th><th>OHIP credential</th><th>OHIP hotel id</th><th>External system</th>
                                <th>Ready</th><th>Last run</th><th>Last tick</th><th>Polls (last 24 h)</th>{canEdit && <th />}
                            </tr>
                        </thead>
                        <tbody>
                            {hotels.length === 0 && (
                                <tr><td colSpan={canEdit ? 10 : 9}>No hotel implements this template yet.</td></tr>
                            )}
                            {hotels.map(h => {
                                const src = h.readiness.source || {};
                                return (
                                    <tr key={h.id}>
                                        <td>{h.hotelname || h.name}</td>
                                        <td>{h.status}</td>
                                        <td>{src.credentialName ? <span className="scq-mono">{src.credentialName}</span> : '—'}</td>
                                        <td>{src.hotelId ? <span className="scq-mono">{src.hotelId}</span> : '—'}</td>
                                        <td>{src.externalSystem ? <span className="scq-mono">{src.externalSystem}</span> : '—'}</td>
                                        <td>{h.readiness.ready
                                            ? <span className="scq-status scq-status-returned">Ready</span>
                                            : <><span className="scq-status scq-status-error">Not ready</span>
                                                <div className="scq-meta">{h.readiness.problems.join('; ')}</div></>}
                                            {h.status !== 'active' && <div className="scq-meta">not polled until the implementation is active</div>}</td>
                                        <td>{formatDateTime(h.last_run_at)}</td>
                                        <td>{h.last_run_at
                                            ? `${h.last_events ?? 0} event(s) · ${h.last_written ?? 0} carried · ${h.last_skipped ?? 0} skipped`
                                            : '—'}
                                            {h.last_error && <div className="scq-meta">{formatDateTime(h.last_error_at)}: {h.last_error}</div>}
                                            {cache && <div className="scq-meta">cache filled {formatDateTime(h.last_hydrated_at, 'not yet')}</div>}</td>
                                        {/* THE CADENCE: how often it was actually polled, and the last polls as a strip. */}
                                        <td>{h.cadence
                                            ? <>
                                                <div>{h.cadence.polls} poll(s) · usually every {seconds(h.cadence.typicalIntervalSeconds)}</div>
                                                <div className="scq-meta">longest gap {seconds(h.cadence.longestGapSeconds)} · {h.cadence.events} event(s)
                                                    {h.cadence.busy ? ` · ${h.cadence.busy} skipped (still running)` : ''}
                                                    {h.cadence.errors ? ` · ${h.cadence.errors} failed` : ''}</div>
                                                <div className="epc-strip">
                                                    {h.cadence.recent.map(t => (
                                                        <span key={t.at} className={`epc-tick epc-${TICK_CLASS(t)}`}
                                                              title={`${formatDateTime(t.at)} — ${TICK_WORDS(t)}${t.ms != null ? ` (${seconds(t.ms / 1000)})` : ''}`} />
                                                    ))}
                                                </div>
                                            </>
                                            : 'not polled in the last 24 h'}</td>
                                        {canEdit && <td>
                                            <Button variant="secondary" className="sa-only" disabled={!!busy || h.status !== 'active'}
                                                    title={h.status !== 'active' ? 'Only an active implementation is polled — its events would be dropped otherwise'
                                                        : `Poll ${h.hotelname || h.name} now`}
                                                    onClick={() => pollNow([h.id])}>
                                                {busy === `poll-${h.id}` ? 'Queuing…' : 'Poll now'}
                                            </Button>
                                        </td>}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <div className="epc-legend">
                        <span><span className="epc-tick epc-events" />carried events</span>
                        <span><span className="epc-tick epc-ok" />nothing waiting</span>
                        <span><span className="epc-tick epc-busy" />skipped, still running</span>
                        <span><span className="epc-tick epc-skipped" />not ready</span>
                        <span><span className="epc-tick epc-error" />failed</span>
                    </div>

                    <div className="scq-meta">
                        OHIP hands each business event over once: while this poller is enabled, nothing else dequeuing the
                        same external system for these hotels will see them.{cache ? ' Prices cost one OHIP call per night per adult count.' : ''}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TemplateEventDeliveryPage;
